import _ from 'lodash'

import api from '@/api'
import constants from '@/constants'

import types from './organizationTypes'
const REV_TYPES = Object.freeze([
  constants.exploreEntityTypes.improvementIdea,
  constants.exploreEntityTypes.problemSolving,
  constants.exploreEntityTypes.quickAction,
])

const actions = {
  getAllOrganizations: async function(context, payload) {
    try {
      context.dispatch('showLoader')
      context.commit(types.CLEAR_ORGANIZATIONS)
      const options = {}
      if (payload.page) options.page = payload.page
      if (payload.limit) options.limit = payload.limit
      const response = await api.organization.getAll(options)

      context.commit(types.SET_ORGANIZATIONS, {
        organizations: response.data.results,
      })

      context.dispatch('hideLoader')
      return response.data
    } catch (err) {
      context.dispatch('hideLoader')
      throw err
    }
  },

  getOrganization: function(context, payload) {
    context.commit(types.CLEAR_ACTIVE_ORGANIZATION)

    return api.organization
      .getById(payload.organizationId)
      .then(response => {
        context.commit(types.SET_ACTIVE_ORGANIZATION, {
          organization: response.data,
        })

        if (response.data.logo) {
          context.commit(
            types.SET_ORGANIZATION_LOGO,
            response.data?.logo ?? null
          )
        }

        if (response.data.mobileLogo) {
          context.commit(
            types.SET_ORGANIZATION_MOBILE_LOGO,
            response.data?.mobileLogo ?? null
          )
        }

        return response.data
      })
      .catch(err => {
        throw err
      })
  },

  retrieveOrganization: function(context, payload) {
    return api.organization
      .getById(payload.organizationId)
      .then(response => response.data)
  },

  getOrganizationStructure: function(context, payload) {
    if (!payload.organizationId) return

    context.commit(types.CLEAR_ORGANIZATION_STRUCTURE)

    return api.organization
      .getOrganizationStructure(payload.organizationId)
      .then(response => {
        context.commit(types.SET_ORGANIZATION_STRUCTURE, {
          structure: response.data,
        })

        return response.data
      })
      .catch(err => {
        throw err
      })
  },

  getOrganizationCategories: function(context, payload) {
    context.commit(types.CLEAR_ORGANIZATION_CATEGORIES)
    return api.category
      .getOrganizationCatalog(payload.organizationId)
      .then(response => {
        context.commit(types.SET_ORGANIZATION_CATEGORIES, {
          categories: response.data,
        })
        return response.data
      })
      .catch(err => {
        throw err
      })
  },

  getOrganizationSituationTypes: function(context, payload) {
    context.commit(types.CLEAR_ORGANIZATION_SITUATION_TYPES)
    return api.situationType
      .getOrganizationCatalog(payload.organizationId)
      .then(response => {
        context.commit(types.SET_ORGANIZATION_SITUATION_TYPES, {
          situationTypes: response.data,
        })
        return response.data
      })
      .catch(err => {
        throw err
      })
  },

  getOrganizationCountryCodes: function(context, payload) {
    context.commit(types.CLEAR_ORGANIZATION_COUNTRY_CODES)
    return api.country
      .getAllCountryCodes(payload.organizationId)
      .then(response => {
        context.commit(types.SET_ORGANIZATION_COUNTRY_CODES, {
          countryCodes: response.data,
        })

        return response.data
      })
      .catch(err => {
        throw err
      })
  },

  clearOrganizationAccounts: function(context) {
    context.commit(types.CLEAR_ORGANIZATION_ACCOUNTS)
  },

  setOrganizationAccounts: function(context, results) {
    context.commit(types.SET_ORGANIZATION_ACCOUNTS, {
      accounts: results,
    })
  },

  getOrganizationRevs: async function(context, payload) {
    const hasTerms = Boolean(payload.terms)

    const response = await api.explore.getContent({
      organizationId: payload.organizationId,
      terms: payload.terms,
      limit: payload.limit,
      from: payload.from,
      filters: { types: REV_TYPES },
      sort: hasTerms
        ? { by: '_score', order: 'desc' }
        : payload?.sort ?? { by: 'updatedTimestamp', order: 'desc' },
      expansions: ['owner', 'coach'],
    })

    return {
      results: response.data.results.map(result => result.response),
      totalRecordsInData: response.data.totalRecordsInData,
    }
  },

  removeOrganizationRev: function(context, payload) {
    context.dispatch('showLoader')

    return api.rev
      .remove(payload.organizationId, payload.revId)
      .then(response => {
        context.dispatch('hideLoader')

        context.commit(types.REMOVE_ORGANIZATION_REV, {
          revId: payload.revId,
        })

        return response.data
      })
      .catch(err => {
        context.dispatch('hideLoader')
        throw err
      })
  },

  createOrganization: function(context, payload) {
    context.dispatch('showLoader')

    return api.organization
      .create(payload.data)
      .then(response => {
        context.dispatch('hideLoader')
        return response.data
      })
      .catch(err => {
        context.dispatch('hideLoader')
        throw err
      })
  },

  createOrganizationAccount: function(context, payload) {
    context.dispatch('showLoader')
    return api.organization
      .createAccount(payload.organizationId, payload.data)
      .then(response => {
        context.dispatch('hideLoader')

        context.commit(types.ADD_ORGANIZATION_ACCOUNT, {
          account: response.data,
        })

        return response.data
      })
      .catch(err => {
        context.dispatch('hideLoader')
        throw err
      })
  },

  updateOrganizationStatus: function(context, payload) {
    context.dispatch('showLoader')

    const data = {
      isEnabled: payload.isEnabled,
    }

    return api.organization
      .updateSettings(payload.organizationId, data)
      .then(response => {
        context.dispatch('hideLoader')

        context.commit(types.SET_ORGANIZATION_STATUS, {
          organizationId: payload.organizationId,
          isEnabled: payload.isEnabled,
        })

        const organization = response.data
        const organizations = _.cloneDeep(context.state.organizations).filter(
          org => {
            if (org.name === organization.name) {
              org.isEnabled = !org.isEnabled
            }

            return org
          }
        )

        context.commit(types.SET_ORGANIZATIONS, {
          organizations: organizations,
        })

        return response.data
      })
      .catch(err => {
        context.dispatch('hideLoader')
        throw err
      })
  },

  updateOrganization: function(context, payload) {
    context.dispatch('showLoader')

    return api.organization
      .update(payload.organizationId, payload.data)
      .then(response => {
        context.dispatch('hideLoader')

        context.commit(types.SET_ACTIVE_ORGANIZATION, {
          organization: response.data,
        })

        return response.data
      })
      .catch(err => {
        context.dispatch('hideLoader')
        throw err
      })
  },

  updateOrganizationSettings: function(context, payload) {
    context.dispatch('showLoader')

    if (payload.data.samlFieldMappings) {
      payload.data.samlFieldMappings = JSON.stringify(
        payload.data.samlFieldMappings
      )
    }

    if (payload.data.samlGroupMappings) {
      payload.data.samlGroupMappings = JSON.stringify(
        payload.data.samlGroupMappings
      )
    }

    return api.organization
      .updateSettings(payload.organizationId, payload.data)
      .then(response => {
        context.dispatch('hideLoader')
        return response.data
      })
      .catch(err => {
        context.dispatch('hideLoader')
        throw err
      })
  },

  setOrganizationLogo: function(context, mediaObject) {
    context.commit(types.SET_ORGANIZATION_LOGO, mediaObject)
  },

  setOrganizationMobileLogo: function(context, mediaObject) {
    context.commit(types.SET_ORGANIZATION_MOBILE_LOGO, mediaObject)
  },
}

export default actions
